import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { readSurveySharingPreference } from '@api/user';
import { updateSurveySharingPreference } from '@redux/slices/user';
import { Announcement, Button, Checkbox, toast } from '@utilities';

import styles from './_index.module.scss';

const VerifiedVoicesSharing = () => {
  const dispatch = useDispatch();

  const [initialShareAutomatically, setInitialShareAutomatically] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdatingSurveySharingPreference, setIsUpdatingSurveySharingPreference] = useState(false);
  const [shareAutomatically, setShareAutomatically] = useState(false);

  const getSurveySharingPreference = async () => {
    setIsLoading(true);
    setLoadError('');

    try {
      const response = await readSurveySharingPreference();
      const nextValue = response?.data?.surveySharingAutomatically ?? false;

      setInitialShareAutomatically(nextValue);
      setShareAutomatically(nextValue);
    } catch (error) {
      console.error(error, `Could not read user's survey sharing preference`);
      setLoadError('We could not load your survey sharing preference. Please try again.');
      toast('Failed to load survey sharing preference.', { status: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getSurveySharingPreference();
  }, []);

  const applyChanges = async () => {
    setIsUpdatingSurveySharingPreference(true);

    try {
      await dispatch(
        updateSurveySharingPreference({
          surveySharingAutomatically: shareAutomatically,
        })
      );
      setInitialShareAutomatically(shareAutomatically);
      toast('Survey sharing preference updated successfully.', { status: 'success' });
    } catch (error) {
      console.error(error, `Could not update user's survey sharing preference`);
      toast('Failed to update survey sharing preference.', { status: 'error' });
    } finally {
      setIsUpdatingSurveySharingPreference(false);
    }
  };

  const isDisabled = isLoading || !!loadError || initialShareAutomatically === shareAutomatically;

  return (
    <div className={styles['verified-voices-sharing']}>
      {loadError && <Announcement text={loadError} variant="error" />}
      <Checkbox
        data-testid="verified-voices-sharing-automatic-sharing"
        isChecked={shareAutomatically}
        isDisabled={isLoading || !!loadError || isUpdatingSurveySharingPreference}
        label="Automatically share all new surveys I create with my organization"
        onChange={() => setShareAutomatically((prevShareAutomatically) => !prevShareAutomatically)}
        value={shareAutomatically}
      />
      <div className={styles['verified-voices-sharing-actions']}>
        {loadError && (
          <Button
            isDisabled={isLoading || isUpdatingSurveySharingPreference}
            onClick={getSurveySharingPreference}
            text="Retry"
            variant="secondary"
          />
        )}
        <Button
          data-testid="verified-voices-sharing-apply-changes"
          isDisabled={isDisabled}
          isLoading={isUpdatingSurveySharingPreference}
          onClick={applyChanges}
          text="Save"
        />
      </div>
    </div>
  );
};

export default VerifiedVoicesSharing;
